import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { asyncScheduler, Observable, Subscription, zip } from 'rxjs';
import { defaultIfEmpty, map, mergeMap, subscribeOn } from 'rxjs/operators';
import {
  ArcGisJobModelLike,
  ArcGisJobStatus,
} from '../models/arcgis-job.model';
import { HttpUtils } from '../utils/http.utils';

const checkIntervalDefault = 1000;

@Injectable()
export class ArcGisJobService {
  constructor(private http: HttpClient) {}

  executeJobAndReturnResult<T = any>(
    url: string,
    body: any,
    resultKey: string,
    checkInterval = checkIntervalDefault
  ): Observable<T> {
    return this.executeJobAndReturnResults<T>(
      url,
      body,
      [resultKey],
      checkInterval
    ).pipe(
      map(([res]) => {
        return res;
      })
    );
  }

  executeJobAndReturnResults<T = any>(
    url: string,
    body: any,
    resultKeys?: string[],
    checkInterval = checkIntervalDefault
  ): Observable<T[]> {
    return this.executeJob(url, body, checkInterval).pipe(
      mergeMap<ArcGisJobModelLike, Observable<T[]>>((job) => {
        const { jobId, results } = job;
        let keys = Object.keys(results);
        if (resultKeys) {
          keys = keys.filter((x) => resultKeys.includes(x as any));
        }

        const requestsObservables: Observable<T>[] = keys
          .map((field) => {
            const paramUrl: string = results[field]?.paramUrl;
            if (!paramUrl) {
              return null;
            }

            return this.http.get<T>(`${url}/jobs/${jobId}/${paramUrl}?f=json`);
          })
          .filter((x) => !!x);

        return zip
          .apply(zip, requestsObservables as any)
          .pipe(defaultIfEmpty([])) as any;
      })
    );
  }

  executeJob(
    url: string,
    body: any,
    checkInterval = checkIntervalDefault
  ): Observable<ArcGisJobModelLike> {
    return this.submitJob(url, body).pipe(
      mergeMap((job) => {
        const jobId = job.jobId;
        return new Observable<ArcGisJobModelLike>((subscriber) => {
          subscriber.add(
            this.observeJob(url, jobId, checkInterval).subscribe({
              next: (res) => {
                if (res?.jobStatus === ArcGisJobStatus.Failed) {
                  const errorResponse = HttpUtils.createBadRequest({
                    error: res,
                  });
                  subscriber.error(errorResponse);
                } else if (res?.jobStatus === ArcGisJobStatus.Success) {
                  subscriber.next(res);
                }
              },
              complete: () => {
                subscriber.complete();
              },
              error: (err) => {
                subscriber.error(err);
              },
            })
          );
        });
      })
    );
  }

  observeJob(url: string, jobId: string, checkInterval = checkIntervalDefault) {
    return new Observable<ArcGisJobModelLike>((subscriber) => {
      let periodicSubscription: Subscription;

      subscriber.add(() => {
        periodicSubscription?.unsubscribe();
      });

      const requestObservable = this.http
        .get<ArcGisJobModelLike>(`${url}/jobs/${jobId}?f=json`)
        .pipe(subscribeOn(asyncScheduler, checkInterval));

      const checkPeriodicStatus = () => {
        periodicSubscription = requestObservable.subscribe({
          next: (res) => {
            subscriber.next(res);

            if (
              res?.jobStatus === ArcGisJobStatus.Success ||
              res?.jobStatus === ArcGisJobStatus.Failed
            ) {
              subscriber.complete();
            } else {
              checkPeriodicStatus();
            }
          },
          error: (response) => {
            subscriber.error(response);
          },
        });
      };

      checkPeriodicStatus();
    });
  }

  submitJob(url: string, body: any): Observable<ArcGisJobModelLike> {
    return this.http.post<ArcGisJobModelLike>(`${url}/submitJob`, body);
  }
}
